import { TableColumn } from '@shared/components/data-table/data-table.component';
import { FormFieldConfig } from '@shared/components/search-form/search-form.component';

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const SKU_DEFAULT_COLUMNS = {
  CATEGORY: 'productCategory',
  SPU_CODE: 'productCode',
  SPU_NAME: 'productName',
  SKU_CODE: 'skuCode',
  SKU_NAME: 'skuName',
  STATUS: 'status',
  VENDOR: 'vendor',
  COST_PRICE: 'costPrice',
  SELLING_PRICE: 'sellingPrice',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const SKU_FLEXIBLE_COLUMNS = {
  REMARK: 'remarks',
  CREATION_DATE: 'creationDate',
  ACTIVITY_PRICE: 'activityPrice',
  MARKET_PRICE: 'marketPrice',
  DISTRIBUTION_PRICE: 'distributionPrice',
  WHOLESALE_PRICE: 'wholesalePrice',
  CLEARING_PRICE: 'clearingPrice',
} as const;

/**
 * Action column property name
 */
export const SKU_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const SKU_DEFAULT_COLUMNS_ORDER: string[] = [
  SKU_DEFAULT_COLUMNS.CATEGORY,
  SKU_DEFAULT_COLUMNS.SPU_CODE,
  SKU_DEFAULT_COLUMNS.SPU_NAME,
  SKU_DEFAULT_COLUMNS.SKU_CODE,
  SKU_DEFAULT_COLUMNS.SKU_NAME,
  SKU_DEFAULT_COLUMNS.STATUS,
  SKU_DEFAULT_COLUMNS.VENDOR,
  SKU_DEFAULT_COLUMNS.COST_PRICE,
  SKU_DEFAULT_COLUMNS.SELLING_PRICE,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const SKU_FLEXIBLE_COLUMNS_ORDER: string[] = [
  SKU_FLEXIBLE_COLUMNS.REMARK,
  SKU_FLEXIBLE_COLUMNS.CREATION_DATE,
  SKU_FLEXIBLE_COLUMNS.ACTIVITY_PRICE,
  SKU_FLEXIBLE_COLUMNS.MARKET_PRICE,
  SKU_FLEXIBLE_COLUMNS.DISTRIBUTION_PRICE,
  SKU_FLEXIBLE_COLUMNS.WHOLESALE_PRICE,
  SKU_FLEXIBLE_COLUMNS.CLEARING_PRICE,
];

export const SKU_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'sku_management.table.columns.product_category',
    prop: SKU_DEFAULT_COLUMNS.CATEGORY,
    width: 180,
    type: 'custom',
    template: 'categoryTemplate',
  },
  {
    label: 'sku_management.table.columns.product_code',
    prop: SKU_DEFAULT_COLUMNS.SPU_CODE,
    width: 110,
  },
  {
    label: 'sku_management.table.columns.product_name',
    prop: SKU_DEFAULT_COLUMNS.SPU_NAME,
    width: 150,
  },
  {
    label: 'sku_management.table.columns.sku_code',
    prop: SKU_DEFAULT_COLUMNS.SKU_CODE,
    width: 110,
  },
  {
    label: 'sku_management.table.columns.sku_name',
    prop: SKU_DEFAULT_COLUMNS.SKU_NAME,
    width: 110,
  },
  {
    label: 'sku_management.table.columns.status',
    prop: SKU_DEFAULT_COLUMNS.STATUS,
    width: 130,
    type: 'custom',
    template: 'statusTemplate',
  },
  {
    label: 'sku_management.table.columns.vendor',
    prop: SKU_DEFAULT_COLUMNS.VENDOR,
    width: 150,
    type: 'custom',
    template: 'vendorTemplate',
  },
  {
    label: 'sku_management.table.columns.cost_price',
    prop: SKU_DEFAULT_COLUMNS.COST_PRICE,
    width: 120,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'sku_management.table.columns.selling_price',
    prop: SKU_DEFAULT_COLUMNS.SELLING_PRICE,
    width: 120,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'sku_management.table.columns.remarks',
    prop: SKU_FLEXIBLE_COLUMNS.REMARK,
    width: 120,
  },
  {
    label: 'sku_management.table.columns.creation_date',
    prop: SKU_FLEXIBLE_COLUMNS.CREATION_DATE,
    width: 150,
    type: 'custom',
    template: 'dateTemplate',
  },
  {
    label: 'sku_management.table.columns.activity_price',
    prop: SKU_FLEXIBLE_COLUMNS.ACTIVITY_PRICE,
    width: 120,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'sku_management.table.columns.market_price',
    prop: SKU_FLEXIBLE_COLUMNS.MARKET_PRICE,
    width: 120,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'sku_management.table.columns.distribution_price',
    prop: SKU_FLEXIBLE_COLUMNS.DISTRIBUTION_PRICE,
    width: 120,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'sku_management.table.columns.wholesale_price',
    prop: SKU_FLEXIBLE_COLUMNS.WHOLESALE_PRICE,
    width: 120,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'sku_management.table.columns.clearing_price',
    prop: SKU_FLEXIBLE_COLUMNS.CLEARING_PRICE,
    width: 120,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'sku_management.table.columns.operation',
    prop: SKU_ACTION_COLUMN.ACTIONS,
    width: 140,
    type: 'actions',
  },
];

// ============================================================================
// SEARCH CONFIGURATION
// ============================================================================

/**
 * Base search fields configuration (always shown)
 */
export const SKU_BASE_SEARCH_FIELDS: FormFieldConfig[] = [
  {
    key: 'productCode',
    label: 'sku_management.search.fields.product_code',
    type: 'text',
    placeholder: 'sku_management.search.placeholders.please_enter',
  },
  {
    key: 'productName',
    label: 'sku_management.search.fields.product_name',
    type: 'text',
    placeholder: 'sku_management.search.placeholders.please_enter',
  },
  {
    key: 'skuCode',
    label: 'sku_management.search.fields.sku_code',
    type: 'text',
    placeholder: 'sku_management.search.placeholders.please_enter',
  },
  {
    key: 'skuName',
    label: 'sku_management.search.fields.sku_name',
    type: 'text',
    placeholder: 'sku_management.search.placeholders.please_enter',
  },
];

/**
 * Status field configuration (shown conditionally)
 */
export const SKU_STATUS_SEARCH_FIELD: FormFieldConfig = {
  key: 'status',
  label: 'sku_management.search.fields.status',
  type: 'multiselect',
  placeholder: 'sku_management.search.placeholders.please_select',
  // Note: options should be provided dynamically in the component
};

/**
 * Get search fields configuration based on active tab
 * @param activeTab - The active tab type ('ordinary' | 'package' | 'draft')
 * @param statusOptions - Status options for the status field (if needed)
 * @returns FormFieldConfig array
 */
export function getSKUSearchConfig(
  activeTab: 'ordinary' | 'package' | 'draft',
  statusOptions?: any[]
): FormFieldConfig[] {
  const fields = [...SKU_BASE_SEARCH_FIELDS];

  // Draft tab only ever contains Draft-status SKUs, so the status filter is redundant there
  if (activeTab !== 'draft') {
    fields.push({
      ...SKU_STATUS_SEARCH_FIELD,
      options: statusOptions || [],
    });
  }

  return fields;
}

// An Active/Inactive filter is intentionally NOT wired up yet.
// GoodsSkuControllerService.getList3 (generated OpenAPI SDK) has no showFlag
// query param — the backend now supports it (see GoodsSkuPageDTO.showFlag),
// but picking it up on the FE needs `npm run api:generate` against a live
// backend (network access not available in this session). Hand-rewriting the
// main SKU search call to bypass the generated SDK just for this one field
// would replace a working, high-traffic code path with untested duplicate
// logic — out of proportion to the benefit. Once regenerated, add a
// `showFlag` FormFieldConfig here and pass it through in
// SKURealApiService.searchSkuItemsPaginated.
